if (!customElements.get('load-more')) {
  customElements.define('load-more', class LoadMore extends HTMLElement {
    constructor() {
      super();

      this.link = this.querySelector('[data-load-more-link]');
      this.spinner = this.querySelector('.loading-overlay__spinner');
      this.text = this.querySelector('.load-more-text');
      this.currentCountEl = this.querySelector('.load-more-current');
      this.isLoading = false;

      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }

      if (this.link) {
        this.link.addEventListener('click', (event) => {
          event.preventDefault();
          this.loadNextPage();
        });
      }
    }

    connectedCallback() {
      const restored = this.restoreState();

      if (!restored) {
        this.checkInitialPage();
      }

      this.setupProductClickTracking();
      this.updateCount();
      this.syncLink();
    }

    get sectionId() {
      return this.getAttribute('data-section-id');
    }

    get nextUrl() {
      return this.getAttribute('data-next-url');
    }

    get cacheKey() {
      return `${window.location.pathname}${window.location.search}`;
    }

    setLoading(loading) {
      this.isLoading = loading;

      if (this.text) {
        this.text.classList.toggle('hidden', loading);
      }

      if (this.spinner) {
        this.spinner.classList.toggle('hidden', !loading);
      }

      if (this.link) {
        this.link.setAttribute('aria-busy', loading ? 'true' : 'false');
        this.link.classList.toggle('disabled', loading);
      }
    }

    syncLink() {
      if (!this.link) return;

      if (this.nextUrl) {
        this.link.href = this.nextUrl;
        this.link.removeAttribute('aria-disabled');
      } else {
        this.link.removeAttribute('href');
        this.link.setAttribute('aria-disabled', 'true');
      }
    }

    updateCount() {
      const grid = document.getElementById('product-grid');

      if (grid && this.currentCountEl) {
        this.currentCountEl.textContent = grid.children.length;
      }
    }

    getFetchUrl(url) {
      const fetchUrl = new URL(url, window.location.origin);

      if (this.sectionId) {
        fetchUrl.searchParams.set('section_id', this.sectionId);
      }

      return fetchUrl.toString();
    }

    updateBrowserPage(url) {
      const requestedUrl = new URL(url, window.location.origin);
      const page = requestedUrl.searchParams.get('page');

      if (!page) return;

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('page', page);

      history.replaceState(
        { ...(history.state || {}), infinitePage: page },
        '',
        currentUrl.toString()
      );
    }

    async loadNextPage() {
      const url = this.nextUrl;

      if (!url || this.isLoading) return;

      this.setLoading(true);

      try {
        const response = await fetch(this.getFetchUrl(url), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) {
          throw new Error(`Load more request failed: ${response.status}`);
        }

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        const newProducts = html.querySelectorAll('#product-grid > li');
        const nextTrigger = html.querySelector('load-more');
        const productGrid = document.getElementById('product-grid');

        if (!productGrid || newProducts.length === 0) {
          this.finish();
          return;
        }

        const fragment = document.createDocumentFragment();

        newProducts.forEach((item) => {
          fragment.appendChild(item);
        });

        productGrid.appendChild(fragment);
        this.updateBrowserPage(url);
        this.updateCount();

        if (nextTrigger?.getAttribute('data-next-url')) {
          this.setAttribute(
            'data-next-url',
            nextTrigger.getAttribute('data-next-url')
          );
          this.syncLink();
        } else {
          this.finish();
        }

        document.dispatchEvent(new CustomEvent('collection:products-appended', {
          detail: {
            count: newProducts.length,
            grid: productGrid
          }
        }));
      } catch (error) {
        console.error('Unable to load more products:', error);
      } finally {
        this.setLoading(false);
      }
    }

    finish() {
      this.removeAttribute('data-next-url');
      this.syncLink();
      this.style.display = 'none';
    }

    setupProductClickTracking() {
      if (this.dataset.stateTrackingInitialised === 'true') return;
      this.dataset.stateTrackingInitialised = 'true';

      const saveState = () => {
        const grid = document.getElementById('product-grid');

        if (!grid) return;

        sessionStorage.setItem(
          `infinite_grid_${this.cacheKey}`,
          grid.innerHTML
        );
        sessionStorage.setItem(
          `infinite_scroll_${this.cacheKey}`,
          String(window.scrollY)
        );
        sessionStorage.setItem(
          `infinite_next_${this.cacheKey}`,
          this.nextUrl || ''
        );
      };

      document.addEventListener('click', (event) => {
        const productLink = event.target.closest('#product-grid a[href*="/products/"]');

        if (productLink) {
          saveState();
        }
      });
    }

    restoreState() {
      const gridHtml = sessionStorage.getItem(`infinite_grid_${this.cacheKey}`);
      const scrollY = sessionStorage.getItem(`infinite_scroll_${this.cacheKey}`);
      const nextUrl = sessionStorage.getItem(`infinite_next_${this.cacheKey}`);

      if (!gridHtml) return false;

      const grid = document.getElementById('product-grid');

      if (grid) {
        grid.innerHTML = gridHtml;
      }

      if (nextUrl) {
        this.setAttribute('data-next-url', nextUrl);
        this.style.removeProperty('display');
      } else {
        this.finish();
      }

      this.syncLink();
      this.updateCount();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollY !== null) {
            window.scrollTo({
              top: Number.parseInt(scrollY, 10) || 0,
              behavior: 'auto'
            });
          }
        });
      });

      sessionStorage.removeItem(`infinite_grid_${this.cacheKey}`);
      sessionStorage.removeItem(`infinite_scroll_${this.cacheKey}`);
      sessionStorage.removeItem(`infinite_next_${this.cacheKey}`);

      return true;
    }

    async checkInitialPage() {
      const currentUrl = new URL(window.location.href);
      const initialPage = Number.parseInt(currentUrl.searchParams.get('page'), 10);

      if (!initialPage || initialPage <= 1 || !this.sectionId) return;

      this.setLoading(true);

      try {
        const productGrid = document.getElementById('product-grid');

        if (!productGrid) return;

        const originalFirstChild = productGrid.firstElementChild;
        const fragment = document.createDocumentFragment();

        for (let page = 1; page < initialPage; page += 1) {
          const pageUrl = new URL(window.location.href);
          pageUrl.searchParams.set('page', String(page));
          pageUrl.searchParams.set('section_id', this.sectionId);

          const response = await fetch(pageUrl.toString(), {
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });

          if (!response.ok) continue;

          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          html.querySelectorAll('#product-grid > li').forEach((item) => {
            fragment.appendChild(item);
          });
        }

        if (fragment.childNodes.length > 0) {
          productGrid.insertBefore(fragment, originalFirstChild);
          this.updateCount();
        }
      } catch (error) {
        console.error('Unable to restore previous collection pages:', error);
      } finally {
        this.setLoading(false);
      }
    }
  });
}
